import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { MentionsInput, Mention, SuggestionDataItem } from 'react-mentions';

import Button, { BUTTON_TYPES, BUTTON_SIZES } from 'buyside/components/presentation/Button/button';
import ErrorBoundary from 'buyside/components/presentation/ErrorBoundary/errorBoundary';
import FileUploadWidget, {
  FileType
} from 'buyside/components/presentation/Form/FileUploadWidget/fileUploadWidget';
import ProfileImage, {
  IMAGE_SIZES
} from 'buyside/components/presentation/ProfileImage/profileImage';

import useMentionableAsset from 'buyside/components/container/Mentionable/useMentionableAsset';

import {
  CreateIdeaCommentMutationVariables,
  IdeaCommentsDocument,
  IdeaCommentsQuery,
  Mentionable,
  Maybe,
  useCreateIdeaCommentMutation
} from 'interfaces/graphql-typings';

import styles from './showPageReplyStyles.scss';
import mentionStyling from 'buyside/styles/module/form/mentionStyling';

interface ShowPageReplyFormProps {
  canCommentAnonymously: Boolean;
  isAllocator: Boolean;
  isAuthor: Boolean;
  ideaId: string;
  isIdeaAnonymous: Boolean;
  parentId: string;
  mentionables?: Maybe<Mentionable>[];
  profileImageUrl?: Maybe<string>;
  closeFormOpenThread: () => void;
  closeForm: () => void;
}

const ShowPageReplyForm: React.FC<ShowPageReplyFormProps> = ({
  canCommentAnonymously,
  isAllocator,
  isAuthor,
  ideaId,
  isIdeaAnonymous,
  parentId,
  mentionables,
  profileImageUrl,
  closeFormOpenThread,
  closeForm
}): React.ReactElement => {
  const [mentionableAssets, markup, renderSuggestion, displayTransform] = useMentionableAsset();

  const [isFocused, setIsFocused] = useState(false);
  const [replyBody, setReplyBody] = useState('');

  const [createComment, { error, loading }] = useCreateIdeaCommentMutation({
    update(cache, { data }) {
      const newReply = data?.createComment?.comment;
      if (!newReply) return;

      const cachedQuery = cache.readQuery<IdeaCommentsQuery>({
        query: IdeaCommentsDocument,
        variables: { ideaId, controlName: !isAuthor }
      });
      if (!cachedQuery?.idea.comments) return;

      cache.writeQuery<IdeaCommentsQuery>({
        query: IdeaCommentsDocument,
        data: {
          idea: {
            ...cachedQuery.idea,
            commentsCount: (cachedQuery.idea.commentsCount || 0) + 1,
            comments: { edges: [{ node: newReply }, ...(cachedQuery.idea.comments.edges || [])] }
          }
        },
        variables: { ideaId }
      });
    }
  });

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setValue,
    watch
  } = useForm<
    Omit<CreateIdeaCommentMutationVariables, 'ideaId' | 'files'> & {
      ideaId?: string;
      files: FileType[];
    }
  >({
    defaultValues: {
      body: '',
      ideaId,
      parentId: null,
      anonymous: Boolean(isAllocator) || Boolean(isAuthor && isIdeaAnonymous),
      response: false,
      files: []
    }
  });

  const prepareFiles = (files: FileType[]): string => {
    const uploadedFiles = files.map(file => {
      const { name, base64 } = file;
      const fileData = file.file;
      return {
        name,
        fileName: fileData.name,
        size: fileData.size,
        data: base64
      };
    });
    return JSON.stringify(uploadedFiles);
  };

  const files = (watch('files') as unknown) as FileType[];

  const onSubmit: SubmitHandler<Omit<CreateIdeaCommentMutationVariables, 'ideaId' | 'files'> & {
    ideaId?: string;
    files: FileType[];
  }> = ({ anonymous }): Promise<void> =>
    createComment({
      variables: {
        ideaId,
        parentId,
        body: replyBody,
        anonymous,
        response: null,
        files: prepareFiles(files)
      }
    }).then(() => {
      closeFormOpenThread();
      setIsFocused(false);
    });

  return (
    <ErrorBoundary pageName="comment form">
      <form
        className={styles.form}
        onFocus={(): void => setIsFocused(true)}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={styles.formContainer}>
          <div className={styles.formLeft}>
            <ProfileImage source={profileImageUrl} size={IMAGE_SIZES.SMALL} />
          </div>
          <div className={styles.formRight}>
            <MentionsInput
              {...register('body')}
              allowSpaceInQuery
              disabled={isSubmitting || loading}
              id="idea-comment-reply-body"
              onChange={(e): void => {
                setReplyBody(e.target.value);
                setValue('body', e.target.value, { shouldDirty: true });
              }}
              placeholder="Post your reply"
              style={mentionStyling}
              value={replyBody}
            >
              <Mention
                appendSpaceOnAdd
                data={mentionables?.filter(Boolean) as SuggestionDataItem[]}
                style={{ backgroundColor: '#DEEDF5' }}
                trigger="@"
              />
              <Mention
                appendSpaceOnAdd
                data={mentionableAssets}
                displayTransform={displayTransform}
                markup={markup}
                renderSuggestion={renderSuggestion}
                style={{ backgroundColor: 'rgb(255, 201, 201)' }}
                trigger="$"
              />
            </MentionsInput>
            {isFocused && (
              <>
                {errors.body && <div className={styles.error}>{errors.body.message}</div>}
                <FileUploadWidget
                  data-testid="fileUploadWidget"
                  setFieldValue={setValue}
                  uploadedFiles={files}
                />
                <div className={styles.fieldNote}>
                  <div>
                    <strong>Type '@'</strong> to tag participants in this comment thread, users you
                    follow, or users that are following you. Tagged members will be notified.
                  </div>
                  <div>
                    <strong>Type '$'</strong> and begin typing to tag any ticker on SumZero. Members
                    have watchlisted the tagged ticker will be notified.
                  </div>
                </div>
                {((!isAuthor && !isAllocator && canCommentAnonymously) ||
                  (isAuthor && isIdeaAnonymous)) && (
                  <label className={styles.checkbox}>
                    <input
                      {...register('anonymous')}
                      disabled={isSubmitting || loading}
                      id="anonymous"
                      type="checkbox"
                    />
                    Post this reply anonymously
                  </label>
                )}
                {error && <div className={styles.error}>{error.graphQLErrors[0].message}</div>}
              </>
            )}
            <div className={styles.buttonArea}>
              <Button
                disabled={!replyBody}
                id="post-idea-reply-comment"
                isLoading={isSubmitting || loading}
                size={BUTTON_SIZES.SMALL}
                text="Post Reply"
                type={BUTTON_TYPES.PRIMARY}
              />
              <Button
                disabled={isSubmitting || loading}
                onClick={closeForm}
                size={BUTTON_SIZES.SMALL}
                text="Cancel"
                type={BUTTON_TYPES.SECONDARY}
              />
            </div>
            {!isAuthor && isAllocator && (
              <div className={styles.allocatorNotice}>
                As an allocator, all of your comments are anonymous.
              </div>
            )}
          </div>
        </div>
      </form>
    </ErrorBoundary>
  );
};

export default ShowPageReplyForm;
